// packages/web/src/components/layout/HeaderBranding.tsx
// Dynamic header branding component for Aithon and partner catalogs

import { Box, Link, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { BrandLogo } from '../common/BrandLogo';

import { getCatalogBranding } from '@/lib/catalog-detection';

// Helper function to get default website URL from environment
const getDefaultWebsite = (): string => {
  if (process.env.NEXT_PUBLIC_BRAND_WEBSITE) {
    return process.env.NEXT_PUBLIC_BRAND_WEBSITE;
  }
  if (process.env.NEXT_PUBLIC_API_URL) {
    try {
      const url = new URL(process.env.NEXT_PUBLIC_API_URL);
      return url.origin;
    } catch {
      // Fall through to default
    }
  }
  return 'https://visionarynow.com';
};

interface HeaderBrandingProps {
  /** Optional catalog branding override */
  branding?: {
    name?: string;
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
    website?: string;
  };
  /** Whether to show as clickable link */
  clickable?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** Whether to show the brand name text (false for widget/embed mode) */
  showName?: boolean;
  /** Optional internal destination when the logo is clicked */
  href?: string;
}

const HeaderBranding: React.FC<HeaderBrandingProps> = ({
  branding: overrideBranding,
  clickable = true,
  size = 'medium',
  showName = true,
  href,
}) => {
  const [catalogBranding, setCatalogBranding] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Calculate logo size early for loading state
  const logoSize = size === 'xlarge' ? 70 : size === 'large' ? 44 : size === 'small' ? 28 : 36;
  const logoHeight = logoSize;
  const logoMaxWidth = size === 'xlarge' ? 272 : size === 'large' ? 220 : size === 'small' ? 120 : 180;
  // Keep loading skeleton width stable without forcing final logo box shape
  const logoWidth = logoMaxWidth;

  useEffect(() => {
    // Only load branding if overrideBranding is not provided AND is not null
    // null means "we know there's no branding", undefined means "not specified"
    if (overrideBranding !== undefined && overrideBranding !== null) {
      // When overrideBranding is provided, use it directly and skip loading state
      setCatalogBranding(overrideBranding);
      setLoading(false);
    } else {
      // Fetch branding from catalog detection
      const loadBranding = async () => {
        try {
          setLoading(true);
          const branding = await getCatalogBranding();
          setCatalogBranding(branding);
        } catch {
          // Fallback to Aithon branding on error
          setCatalogBranding({
            name: 'Aithon',
            logo: '/assets/brands/default/logo.webp',
            primaryColor: '#D4AF37',
            website: getDefaultWebsite(),
          });
        } finally {
          setLoading(false);
        }
      };
      loadBranding();
    }
  }, [overrideBranding]);

  // Show loading skeleton only when actually loading (no override branding)
  if (loading && !catalogBranding) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: logoWidth,
          height: logoHeight,
        }}
      >
        {/* Show a subtle skeleton instead of gray box */}
        <Box
          sx={{
            width: logoWidth,
            height: logoHeight,
            bgcolor: 'rgba(0,0,0,0.04)',
            borderRadius: 1,
          }}
        />
      </Box>
    );
  }

  const branding = catalogBranding || {
    name: 'Aithon',
    logo: '/assets/brands/default/logo.webp',
    primaryColor: '#D4AF37',
    website: getDefaultWebsite(),
  };
  const textVariant =
    size === 'large' || size === 'xlarge' ? 'h6' : size === 'small' ? 'body2' : 'body1';

  // Logo URL for BrandLogo component
  const logoUrl = branding.logo || null;

  // For catalog instances (when branding is provided as override), show only logo
  const isCatalogInstance = !!overrideBranding;

  const brandingContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {logoUrl ? (
        <BrandLogo
          src={logoUrl}
          alt={`${branding.name} logo`}
          height={logoHeight}
          maxWidth={logoMaxWidth}
          preserveNaturalAspect
          lazy={false}
          priority="high"
          prefetch={false}
        />
      ) : (
        <Box
          sx={{
            width: logoWidth,
            height: logoHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f5f5f5',
            border: '1px dashed #ccc',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            no logo
          </Typography>
        </Box>
      )}
      {/* Only show text for catalog instances with custom branding */}
      {showName && isCatalogInstance && overrideBranding?.name && (
        <Typography
          variant={textVariant}
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            '&:hover': clickable
              ? {
                  color: branding.primaryColor || '#D4AF37',
                }
              : {},
          }}
        >
          {branding.name}
        </Typography>
      )}
    </Box>
  );

  if (clickable && (href || branding.website)) {
    const targetHref = href || branding.website;
    const isExternal = !!targetHref && /^https?:\/\//i.test(targetHref);

    return (
      <Link
        href={targetHref}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        sx={{
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'none',
          },
        }}
      >
        {brandingContent}
      </Link>
    );
  }

  return brandingContent;
};

export default HeaderBranding;
